<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onUnmounted, ref, useId, useTemplateRef, watch } from "vue";
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxLabel,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxViewport,
} from "reka-ui";
import {
  breakpointsTailwind,
  createReusableTemplate,
  useBreakpoints,
  useDebounceFn,
  useIntersectionObserver,
} from "@vueuse/core";
import { useI18n } from "vue-i18n";
import BLabel from "./bLabel.vue";
import Loading from "./loading.vue";
import BModal from "./BModal.vue";
import PhCaretDown from "~icons/ph/caret-down";
import PhCheck from "~icons/ph/check";
import PhX from "~icons/ph/x";
import TablerListSearch from "~icons/tabler/list-search";
import SvgSpinners3DotsScale from "~icons/svg-spinners/3-dots-scale";
import { borderColors, labelColors, size as sizeConst } from "@/services/application/constantes";

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    label?: string;
    options: TComboOption[];
    isGroup?: boolean;
    filterFunction?: (value: TComboItem[], search: string) => TComboItem[];
    placeholder?: string;
    asyncSearch?: boolean;
    size?: keyof typeof sizeConst;
    autoFocus?: boolean;
    noValueLabel?: string;
    required?: boolean;
    disabled?: boolean;
    disableSelectedRender?: boolean;
    borderColor?: keyof typeof borderColors;
    by?: string;
  }>(),
  {
    isGroup: false,
    loading: false,
    size: "small",
    filterFunction: (list, search) => {
      if (!search) return list;
      const normalize = (str: string) =>
        str
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
      const normalizedSearch = normalize(search);
      return list.filter((item) => normalize(item.display).includes(normalizedSearch));
    },
    placeholder: "",
    asyncSearch: true,
    autoFocus: false,
    required: false,
    disabled: false,
    disableSelectedRender: false,
    borderColor: "base",
    by: "_id",
  },
);
const emits = defineEmits<{
  search: [options: TSearchOptions];
}>();

const page = defineModel<number>("page", { default: 0, required: false });
const limit = defineModel<number>("limit", { default: 10, required: false });
const haveMore = defineModel<boolean>("haveMore", { default: true });

type TSearchOptions = {
  search: string;
  page: number;
  limit: number;
};

const search = defineModel<string>("search", { default: "", required: false });

const optionsMaybeFiltered = computed(() => {
  if (props.asyncSearch) return props.options;
  return props.filterFunction(props.options, search.value);
});

const triggerSearch = () => {
  if (props.loading || !haveMore.value) return;
  emits("search", { search: search.value, page: page.value, limit: limit.value });
};

const isOpen = ref(false);

const debouceTypeSearch = useDebounceFn(async () => {
  page.value = 0;
  haveMore.value = true;
  await nextTick();
  triggerSearch();
}, 300);

watch(
  () => search.value,
  () => {
    if (!isOpen.value) return;
    debouceTypeSearch();
  },
);

const isEndListReached = useTemplateRef("enOfList");

const { pause, resume } = useIntersectionObserver(isEndListReached, ([{ isIntersecting }]) => {
  if (!isIntersecting || props.loading || !haveMore.value) return;

  if (optionsMaybeFiltered.value?.length > 0) page.value++;

  triggerSearch();
});

watch(
  () => props.loading,
  (newValue) => {
    if (newValue) pause();
    else resume();
  },
);

const { t } = useI18n();
const labelId = useId();

const [UseTemplate, TemplateContent] = createReusableTemplate();

type TComboItem = {
  _id: string;
  display: string;
  [key: string]: any;
};

type TComboValue = TComboItem | TComboItem[];

type TComboOption = TComboItem[] | { _id: string; display: string; items: TComboItem[] };

const selectedValue = defineModel<TComboValue>();

const isAnArray = computed(() => Array.isArray(selectedValue.value));

const showLabel = computed(() => !isAnArray.value && selectedValue.value);

const breakpoints = useBreakpoints(breakpointsTailwind);
const lgAndSmaller = breakpoints.smallerOrEqual("lg");

const swallowGhostEvents = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
};

let swallowTimeout: ReturnType<typeof window.setTimeout> | undefined;
let swallowPreviousPointerEvents = "";

const restoreBodyPointerEvents = (previous: string) => {
  if (!previous || previous === "none")
    document.body.style.removeProperty("pointer-events");
  else
    document.body.style.pointerEvents = previous;
};

const releaseGhostPointerEvents = () => {
  if (swallowTimeout !== undefined) {
    window.clearTimeout(swallowTimeout);
    swallowTimeout = undefined;
  }
  document.removeEventListener("pointerup", swallowGhostEvents, true);
  document.removeEventListener("click", swallowGhostEvents, true);
  restoreBodyPointerEvents(swallowPreviousPointerEvents);
};

const swallowGhostPointerEvents = () => {
  swallowPreviousPointerEvents = document.body.style.pointerEvents;
  document.body.style.pointerEvents = "none";
  document.addEventListener("pointerup", swallowGhostEvents, true);
  document.addEventListener("click", swallowGhostEvents, true);
  swallowTimeout = window.setTimeout(releaseGhostPointerEvents, 100);
};

const unlockBodyPointerEvents = () => {
  if (document.body.style.pointerEvents === "none")
    document.body.style.removeProperty("pointer-events");
};

onBeforeUnmount(() => {
  isOpen.value = false;
  releaseGhostPointerEvents();
});

onUnmounted(() => {
  unlockBodyPointerEvents();
});

// A pick that closes the list (single-select, or multi-select after a search): the ghost pointerup/click of that
// tap (mobile) must not reach what the list covered. A pointerdown outside an open list closes it and that click
// must go through (e.g. "Retirer"), so only such a pick arms the swallow.
// A pointerdown on a modal's backdrop only closes the list, as before: its click would close the modal too
let closingAfterPick = false;
let closingOnBackdrop = false;

const handleItemSelect = () => {
  const hadSearch = !!search.value;
  // Without a search, a multi-select list stays open to pick several options in a row
  closingAfterPick = !isAnArray.value || hadSearch;
  // A pick empties the search: at the next opening the list shows every option again.
  // reka-ui keeps the typed text while the list is open, and the search watcher reloads the options.
  search.value = "";
  // After a search, reka-ui would keep a multi-select list open: it is closed once the pick is applied (and the
  // input focused again), and opens again when the user types or clicks the caret
  if (isAnArray.value && hadSearch) {
    nextTick(() => {
      isOpen.value = false;
      handleOpen(false);
    });
  }
};

const handlePointerDownOutside = (event: { target: EventTarget | null }) => {
  closingOnBackdrop = !!(event.target as Element | null)?.closest?.('[data-b-modal-id] > .backdrop');
};

const handleOpen = (value: boolean) => {
  if (value) {
    closingAfterPick = false;
    closingOnBackdrop = false;
    resume();
    if (!(optionsMaybeFiltered.value?.length > 0)) triggerSearch();
    return;
  }
  pause();
  if (closingAfterPick || closingOnBackdrop) swallowGhostPointerEvents();
  closingAfterPick = false;
  closingOnBackdrop = false;
};

// The phone sheet's own close (its X, Escape): the list closes with it. Without this the sheet hid itself while the
// list stayed open, its header and backdrop left on screen until a second tap
const handleSheetModel = (value: boolean) => {
  if (value) return;
  isOpen.value = false;
  pause();
};

const topDistance = computed(() => {
  if (props.size === "dense") return "0.5rem";
  if (props.size === "small") return "0.5rem";
  if (props.size === "medium") return "0.5rem";
  if (props.size === "large") return "0.5rem";
  return "0.2rem";
});

const removeSelected = (index: number) => {
  if (props.disabled) return;
  selectedValue.value = (selectedValue.value as TComboItem[]).filter((_, i) => i !== index);
};

const clearSingleSelected = () => {
  if (props.disabled) return;
  selectedValue.value = undefined;
};

const showAll = ref(false);
const maxVisibleItems = 3;

const toggleShowAll = () => {
  showAll.value = !showAll.value;
};

const getItemKey = (item: TComboItem, index: number) => {
  const identityValue = props.by ? item?.[props.by] : undefined;

  return identityValue ?? item?._id ?? item?.tempId ?? item?.display ?? index;
};

// Mobile focus handling
const mobileInputRef = useTemplateRef("mobileInput");
watch(
  () => isOpen.value,
  async (newValue) => {
    if (newValue && lgAndSmaller.value) {
      await nextTick();
      setTimeout(() => {
        if (mobileInputRef.value) {
          mobileInputRef.value.$el?.focus?.() || mobileInputRef.value?.focus?.();
        }
      }, 200);
    }
  },
);

// Prevent Escape key from bubbling up to parent components when dropdown is open
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape" && isOpen.value) {
    e.stopPropagation();
    isOpen.value = false;
  }
};
</script>

<template>
  <ComboboxRoot
    v-model:open="isOpen"
    v-model="selectedValue"
    v-model:search-term="search"
    class="combobox-root relative"
    :multiple="isAnArray"
    :display-value="(v) => v?.display ?? ''"
    ignore-filter
    @update:open="handleOpen"
    :disabled="disabled"
    :by="by"
    :aria-busy="loading || undefined"
    :aria-required="required || undefined"
    open-on-focus
    @keydown="handleKeydown"
  >
    <div class="flex flex-wrap gap-1 items-end max-w-full">
      <ComboboxAnchor
        class="combobox-anchor group grow inline-flex items-center justify-between rounded-lg px-3 leading-normal gap-2 bg-base outline-hidden border max-w-full transition-shadow duration-200"
        :class="[
 { 'mt-5': label },
          sizeConst[size],
          borderColors[borderColor],
          disabled
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:ring-1 hover:ring-primary-400/15 dark:hover:ring-primary-400/10 focus-within:ring-1 focus-within:ring-primary-500/25 dark:focus-within:ring-primary-500/15',
        ]"
      >
        <transition name="combobox-fade" mode="out-in">
          <div
            v-if="showLabel"
            class="flex items-center gap-2 min-w-0 grow"
            data-cy="combobox-selected"
          >
            <button
              type="button"
              class="min-w-0 flex items-center gap-2 overflow-hidden grow text-left cursor-pointer bg-transparent border-0 p-0"
              :disabled="disabled"
              @click="isOpen = !isOpen"
            >
              <div
                v-tooltip="(selectedValue as TComboItem).display"
                class="min-w-0 flex items-center gap-2 overflow-hidden"
              >
                <slot name="selected-render" :selected="selectedValue">
                  <span class="text-sm truncate">{{ (selectedValue as TComboItem).display }}</span>
                </slot>
              </div>
            </button>
            <button
              v-if="!disabled"
              type="button"
              class="shrink-0 p-0.5 rounded-full text-gray-400 hover:text-danger-500 hover:bg-danger-500/10 transition-colors"
              :aria-label="t('common.a11y.clearSelection')"
              @click.stop="clearSingleSelected"
            >
              <PhX class="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
          <div v-else class="flex gap-2 grow items-center min-w-0">
            <slot name="search-icon">
              <TablerListSearch
                class="h-4 w-4 shrink-0 subtitle transition-colors group-focus-within:text-primary-500"
                aria-hidden="true"
              />
            </slot>
            <ComboboxInput
              v-model="search"
              :disabled="disabled"
              :auto-focus="autoFocus"
              class="bg-transparent! outline-hidden selection:text-primary-400 placeholder:text-gray-400 dark:placeholder:text-gray-600 grow shrink min-w-5"
              :placeholder="placeholder || t('common.combobox.defaultPlaceholder')"
              :aria-labelledby="label ? labelId : undefined"
              :aria-required="required || undefined"
              data-cy="combobox-input"
              @update:model-value="asyncSearch ? undefined : filterFunction(options, $event)"
            />
          </div>
        </transition>

        <span
          v-if="label"
          :id="labelId"
          class="combobox-label absolute leading-none left-0 transform -translate-y-1/2 subtitle cursor-text line-clamp-1 transition-all duration-200"
        >
          {{ label }} <span v-if="required" class="text-danger-400" aria-hidden="true">*</span>
        </span>

        <ComboboxTrigger
          v-if="!loading"
          class="shrink-0 p-0.5 subtitle transition-all duration-200"
          :class="isOpen ? 'rotate-180 text-primary-500' : ''"
          :aria-label="t('common.a11y.openOptions')"
          data-cy="combobox-trigger"
        >
          <PhCaretDown class="h-4 w-4" aria-hidden="true" />
        </ComboboxTrigger>
        <Loading v-else class="h-4 w-4 shrink-0" aria-hidden="true" />
      </ComboboxAnchor>
      <slot name="options" />
    </div>

    <slot v-if="!selectedValue" name="no-selected-value">
      <BLabel v-if="noValueLabel" class="mt-1" bg-color="disabled">
        {{ noValueLabel }}
      </BLabel>
    </slot>

    <!-- Multi-select chips -->
    <slot
      v-if="isAnArray && !disableSelectedRender"
      name="selected-render-container"
      :selected="selectedValue"
    >
      <div class="flex flex-wrap gap-1.5 mt-2 items-center">
        <TransitionGroup name="chip">
          <template
            v-for="(item, index) in showAll
              ? selectedValue
              : (selectedValue as TComboItem[]).slice(0, maxVisibleItems)"
            :key="getItemKey(item, index as number)"
          >
            <slot
              name="selected-item"
              :selected="item"
              :remove-selected="() => removeSelected(index as number)"
            >
              <span
                class="combobox-chip inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg bg-primary-500/10 dark:bg-primary-500/15 text-sm border border-primary-500/20"
              >
                <slot
                  name="selected-render"
                  :selected="item"
                  :remove-selected="() => removeSelected(index as number)"
                >
                  <span class="truncate max-w-[200px]">{{ item.display }}</span>
                </slot>
                <button
                  v-if="!disabled"
                  type="button"
                  class="shrink-0 p-0.5 rounded-full text-primary-500/60 hover:text-danger-500 hover:bg-danger-500/10 transition-colors"
                  :aria-label="t('common.a11y.removeItem', { item: item.display })"
                  @click="removeSelected(index as number)"
                >
                  <PhX class="h-3 w-3" aria-hidden="true" />
                </button>
              </span>
            </slot>
          </template>
        </TransitionGroup>
        <template v-if="(selectedValue as TComboItem[]).length > maxVisibleItems">
          <span v-if="!showAll" class="subtitle text-xs">
            + {{ (selectedValue as TComboItem[]).length - maxVisibleItems }}
            {{ t("common.combobox.plusResults") }}
          </span>
          <button
            type="button"
            @click.prevent="toggleShowAll"
            class="text-xs text-primary-500 hover:text-primary-600 font-medium transition-colors"
          >
            {{ showAll ? t("common.combobox.showLess") : t("common.combobox.showMore") }}
          </button>
        </template>
      </div>
    </slot>

    <!-- Shared dropdown content (reused between desktop popover and mobile bottom sheet) -->
    <UseTemplate>
      <ComboboxViewport
        class="combobox-viewport p-1 flex flex-col gap-0.5 overflow-x-hidden overflow-y-auto md:max-h-64 max-h-full show-scrollbar"
      >
        <!-- Search bar inside dropdown (shown when a value is selected or on mobile) -->
        <div
          v-if="showLabel || lgAndSmaller"
          class="sticky -top-1 -mx-1 -mt-1 px-1 pt-1 bg-white dark:bg-[#1a2332] z-10 p-1.5 lg:p-1 rounded-t-xl"
        >
          <div
            class="flex items-center gap-2 border border-base rounded-lg px-3 py-2 lg:py-1.5 bg-base focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 transition-all"
          >
            <TablerListSearch class="h-5 w-5 lg:h-4 lg:w-4 shrink-0 subtitle" aria-hidden="true" />
            <ComboboxInput
              ref="mobileInput"
              v-model="search"
              :disabled="disabled"
              :auto-focus="!lgAndSmaller"
              class="bg-transparent! outline-hidden h-full selection:text-primary-400 placeholder:text-gray-400 dark:placeholder:text-gray-600 grow shrink min-w-5 text-base lg:text-sm"
              :placeholder="placeholder || t('common.combobox.defaultPlaceholder')"
              :aria-labelledby="label ? labelId : undefined"
              data-cy="combobox-search"
            />
            <button
              v-if="search"
              type="button"
              class="shrink-0 p-0.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              :aria-label="t('common.a11y.clearSearch')"
              @click="search = ''"
            >
              <PhX class="h-4 w-4 lg:h-3.5 lg:w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <ComboboxEmpty class="py-6 flex flex-col items-center justify-center gap-2">
          <template v-if="loading">
            <Loading class="text-4xl" />
          </template>
          <template v-else>
            <TablerListSearch class="h-8 w-8 subtitle opacity-40" aria-hidden="true" />
            <p class="subtitle text-sm">
              {{ t("common.combobox.noResults") }}
            </p>
          </template>
        </ComboboxEmpty>

        <!-- Grouped items -->
        <template v-if="isGroup">
          <ComboboxGroup v-for="(group, gIdx) in optionsMaybeFiltered" :key="gIdx">
            <ComboboxLabel class="subtitle text-xs font-medium px-2 pt-2 pb-1">
              <slot name="group-label" :group="group">
                {{ group.display }}
              </slot>
            </ComboboxLabel>

            <ComboboxItem
              v-for="(option, oIdx) in group.items"
              :key="oIdx"
              class="combobox-item text-sm leading-none rounded-lg flex items-center relative pl-8 pr-3 py-2 select-none outline-hidden ring-0 data-disabled:opacity-50 data-disabled:pointer-events-none data-highlighted:bg-primary-500/10 data-highlighted:dark:bg-primary-500/15 cursor-pointer data-[state=checked]:text-primary-600 dark:data-[state=checked]:text-primary-400 transition-colors"
              :value="option"
              :disabled="option.disabled === true"
              :data-cy="`combobox-item-${option.display || option._id}`"
              @select="handleItemSelect"
            >
              <ComboboxItemIndicator
                class="absolute left-2 w-5 inline-flex items-center justify-center text-primary-500"
              >
                <PhCheck class="h-4 w-4" aria-hidden="true" />
              </ComboboxItemIndicator>
              <span>{{ option.display }}</span>
            </ComboboxItem>
            <ComboboxSeparator
              v-if="gIdx !== optionsMaybeFiltered?.length - 1"
              class="h-px bg-gray-200 dark:bg-gray-700 mx-2 my-1"
            />
          </ComboboxGroup>
        </template>

        <!-- Flat items -->
        <template v-else>
          <div class="flex flex-col gap-0.5" data-cy="combobox-options">
            <template v-for="(option, index) in optionsMaybeFiltered" :key="option._id || index">
              <ComboboxItem
                class="combobox-item rounded-lg flex items-center relative pl-8 lg:pl-7 pr-3 py-3 lg:py-2 min-w-0 overflow-hidden w-full select-none outline-hidden ring-0 data-disabled:opacity-50 data-disabled:pointer-events-none data-highlighted:bg-primary-500/10 data-highlighted:dark:bg-primary-500/15 cursor-pointer data-[state=checked]:text-primary-600 dark:data-[state=checked]:text-primary-400 active:scale-[0.99] transition-all duration-100"
                :value="option"
                :disabled="option.disabled === true"
                :data-cy="`combobox-item-${option.display || option._id}`"
                @select="handleItemSelect"
              >
                <ComboboxItemIndicator
                  class="absolute left-2 lg:left-1.5 w-5 inline-flex items-center justify-center text-primary-500"
                >
                  <PhCheck class="lg:h-4 lg:w-4" aria-hidden="true" />
                </ComboboxItemIndicator>
                <slot name="custom-item" :item="option">
                  <span class="text-base lg:text-sm">{{ option.display }}</span>
                </slot>
              </ComboboxItem>
            </template>

            <!-- Infinite scroll loader -->
            <template v-if="asyncSearch">
              <div v-if="loading" class="flex justify-center py-3">
                <SvgSpinners3DotsScale class="text-primary-500" />
              </div>
              <span v-if="haveMore && !loading" ref="enOfList" class="h-1 w-full" />
            </template>
          </div>
        </template>
      </ComboboxViewport>
    </UseTemplate>

    <!-- Mobile: bottom sheet -->
    <BModal
      v-if="lgAndSmaller"
      :model-value="isOpen"
      disable-back-drop
      position="bottom"
      @update:model-value="handleSheetModel"
      class="w-full max-w-full rounded-t-2xl rounded-b-none"
      :have-header="true"
    >
      <template #header>
        <div class="flex items-center gap-2">
          <span v-if="label" class="font-medium text-base">{{ label }}</span>
          <span v-else class="font-medium text-base subtitle">{{
            placeholder || t("common.combobox.defaultPlaceholder")
          }}</span>
        </div>
      </template>
      <ComboboxContent as-child>
        <TemplateContent />
      </ComboboxContent>
    </BModal>

    <!-- Desktop: popover dropdown -->
    <ComboboxPortal v-else>
      <ComboboxContent
        style="width: var(--reka-popper-anchor-width)"
        position="popper"
        @pointer-down-outside="handlePointerDownOutside"
        class="combobox-content z-100 flex flex-col w-full mt-1.5 rounded-xl border border-base bg-white/80 dark:bg-[#1a2332]/90 backdrop-blur-xl backdrop-saturate-150 shadow-lg dark:shadow-black/25 will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
      >
        <TemplateContent />
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<style scoped>
/* Anchor label */
.combobox-label {
  left: 0;
  top: v-bind(topDistance);
}

/* Fade transition for selected <-> search toggle */
.combobox-fade-enter-active,
.combobox-fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.combobox-fade-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}
.combobox-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

/* Chip enter/leave transitions */
.chip-enter-active {
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.chip-leave-active {
  transition: all 0.15s ease;
}
.chip-enter-from {
  opacity: 0;
  transform: scale(0.8);
}
.chip-leave-to {
  opacity: 0;
  transform: scale(0.8);
}
.chip-move {
  transition: transform 0.2s ease;
}
</style>
